import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dices, Users, Calendar, Sparkles, User, Shield, Clock, Heart, Mail } from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEO path="/" />
      {/* Hero Section - Logo Only */}
      <section className="relative pt-20 sm:pt-32 pb-12 sm:pb-20 px-4 overflow-hidden min-h-[400px] sm:min-h-[700px] flex items-center justify-center">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_roleplay-hub-30/artifacts/aih218gk_Sans%20titre-2.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
      </section>

      {/* CTA Section avec phrase */}
      <section className="py-8 sm:py-16 px-4 bg-black">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <p className="text-lg sm:text-2xl md:text-3xl text-purple-200 font-light leading-relaxed">
              Association de jeu de rôle sur table et en ligne. <br />
              Ici, on ne raconte pas des histoires. On les vit !
            </p>
          </div>
        </div>
      </section>

      {/* About Section - WHITE BACKGROUND */}
      <section id="about" className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="space-y-8 sm:space-y-12">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-purple-800 tracking-wide mb-8 sm:mb-16">
              À propos de Just Rôle It
            </h2>

            {/* Qui sommes-nous */}
            <Card className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 p-4 sm:p-8 hover:shadow-xl hover:shadow-purple-200/50 transition-all">
              <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 bg-purple-600 rounded-lg flex-shrink-0">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-3xl md:text-4xl font-black text-purple-700 mt-1 sm:mt-2">
                  QUI SOMMES-NOUS ?
                </h3>
              </div>
              <div className="space-y-3 sm:space-y-4 text-gray-700 leading-relaxed text-base sm:text-lg pl-0 md:pl-16">
                <p>
                  <span className="font-bold text-purple-600">Just Rôle It</span> est une association loi 1901 herblaysienne, fondée le <span className="font-semibold text-purple-600">24 septembre 2025</span>.
                  Cependant, l'aventure Just Rôle It a réellement débuté en <span className="font-semibold">avril 2020</span>, pendant le confinement lié à la pandémie de COVID-19.
                </p>
                <p>
                  Nous avions alors créé une plateforme sociale en ligne destinée aux personnes isolées, afin de favoriser les échanges et de maintenir un lien entre les utilisateurs confinés.
                </p>
                <p className="hidden sm:block">
                  Jeux d'échecs en ligne, documentaires en watchparty, jeux de société virtuels…
                </p>
                <p className="hidden sm:block">
                  C'est dans ce contexte que, passionnés de jeu de rôle, nous avons naturellement proposé cette activité à la communauté.
                  Très vite, elle est devenue l'activité principale de la plateforme.
                </p>
                <p>
                  Quelques années plus tard, ce même groupe d'amis a décidé de créer une structure dédiée, ouverte à tous, aussi bien aux initiés qu'aux curieux désireux de découvrir le jeu de rôle : <span className="font-black text-purple-700 text-lg sm:text-xl">Just Rôle It</span>.
                </p>
              </div>
            </Card>

            {/* Qu'est-ce que le jeu de rôle */}
            <Card className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 p-4 sm:p-8 hover:shadow-xl hover:shadow-purple-200/50 transition-all">
              <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 bg-purple-600 rounded-lg flex-shrink-0">
                  <Dices className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-3xl md:text-4xl font-black text-purple-700 mt-1 sm:mt-2">
                  QU'EST-CE QUE LE JEU DE RÔLE ?
                </h3>
              </div>
              <div className="space-y-3 sm:space-y-4 text-gray-700 leading-relaxed text-base sm:text-lg pl-0 md:pl-16">
                <p>
                  Le jeu de rôle, aussi appelé <span className="font-bold text-purple-600">JDR</span>, est un loisir créatif et collaboratif où les participants racontent ensemble une histoire interactive.
                </p>
                <p>
                  Les joueurs y incarnent un personnage fictif évoluant dans une époque ou un univers défini : <span className="font-semibold text-purple-600">contemporain, médiéval, futuriste, fantastique</span>, etc.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                  <div className="bg-purple-100 p-4 rounded-lg border border-purple-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <User className="h-5 w-5 text-purple-600" />
                      <span className="font-bold text-purple-700">Les Joueurs</span>
                    </div>
                    <p className="text-sm text-gray-600">Incarnent des personnages et prennent des décisions</p>
                  </div>
                  <div className="bg-purple-100 p-4 rounded-lg border border-purple-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <Shield className="h-5 w-5 text-purple-600" />
                      <span className="font-bold text-purple-700">Le Meneur de Jeu</span>
                    </div>
                    <p className="text-sm text-gray-600">Décrit le monde et donne vie à l'intrigue</p>
                  </div>
                </div>
                <p>
                  Les univers explorés sont multiples, permettant de découvrir une grande variété de styles et de thématiques, où <span className="font-semibold text-purple-600">l'imagination et la coopération</span> sont au cœur de l'expérience.
                </p>
              </div>
            </Card>

            {/* À qui s'adresse l'association */}
            <Card className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 p-4 sm:p-8 hover:shadow-xl hover:shadow-purple-200/50 transition-all">
              <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 bg-purple-600 rounded-lg flex-shrink-0">
                  <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-3xl md:text-4xl font-black text-purple-700 mt-1 sm:mt-2">
                  À QUI S'ADRESSE L'ASSOCIATION ?
                </h3>
              </div>
              <div className="space-y-4 sm:space-y-6 text-gray-700 leading-relaxed text-base sm:text-lg pl-0 md:pl-16">
                <p>
                  L'association Just Rôle It s'adresse à tous les habitants d'<span className="font-bold text-purple-600">Herblay-sur-Seine et des communes voisines</span>, âgés de <span className="font-bold text-purple-600">14 à 77 ans</span>, passionnés ou simplement curieux de découvrir le jeu de rôle.
                </p>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div className="bg-white p-2 sm:p-4 rounded-lg border-2 border-purple-300 shadow-sm hover:shadow-md transition-all">
                    <div className="text-center">
                      <div className="inline-block p-2 sm:p-3 bg-purple-100 rounded-full mb-2 sm:mb-3">
                        <User className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
                      </div>
                      <p className="font-bold text-purple-700 text-xs sm:text-base">Débutants</p>
                      <p className="text-xs text-gray-600 mt-1 hidden sm:block">Découvrez le JDR</p>
                    </div>
                  </div>
                  <div className="bg-white p-2 sm:p-4 rounded-lg border-2 border-purple-300 shadow-sm hover:shadow-md transition-all">
                    <div className="text-center">
                      <div className="inline-block p-2 sm:p-3 bg-purple-100 rounded-full mb-2 sm:mb-3">
                        <Users className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
                      </div>
                      <p className="font-bold text-purple-700 text-xs sm:text-base">Convivialité</p>
                      <p className="text-xs text-gray-600 mt-1 hidden sm:block">Ambiance bienveillante</p>
                    </div>
                  </div>
                  <div className="bg-white p-2 sm:p-4 rounded-lg border-2 border-purple-300 shadow-sm hover:shadow-md transition-all">
                    <div className="text-center">
                      <div className="inline-block p-2 sm:p-3 bg-purple-100 rounded-full mb-2 sm:mb-3">
                        <Clock className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
                      </div>
                      <p className="font-bold text-purple-700 text-xs sm:text-base">2-3 heures</p>
                      <p className="text-xs text-gray-600 mt-1 hidden sm:block">Par session</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-100 to-purple-50 p-4 sm:p-6 rounded-lg border-l-4 border-purple-600">
                  <p className="text-center text-purple-800 font-semibold text-sm sm:text-base">
                    Chaque session se déroule dans un cadre bienveillant, favorisant la participation, l'écoute et le plaisir de jouer ensemble.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section - BLACK BACKGROUND */}
      <section className="py-12 sm:py-20 px-4 relative bg-black">
        <div className="container mx-auto">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-8 sm:mb-16 tracking-wide">
            Pourquoi nous rejoindre ?
          </h2>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 max-w-7xl mx-auto">
            {/* Feature 1 */}
            <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-4 sm:p-8 hover:border-purple-400/60 transition-all hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.4)] group">
              <div className="flex justify-center mb-4 sm:mb-6">
                <div className="p-3 sm:p-4 bg-purple-500/20 rounded-full group-hover:bg-purple-500/30 transition-all">
                  <Dices className="h-8 w-8 sm:h-12 sm:w-12 text-purple-400 group-hover:drop-shadow-[0_0_15px_rgba(147,51,234,0.8)]" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-purple-300 text-center mb-4">Sessions régulières</h3>
              <p className="text-gray-400 text-center leading-relaxed">
                Participez à des sessions de jeu de rôle chaque semaine avec une communauté passionnée
              </p>
            </Card>

            {/* Feature 2 */}
            <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8 hover:border-purple-400/60 transition-all hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.4)] group">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-purple-500/20 rounded-full group-hover:bg-purple-500/30 transition-all">
                  <Users className="h-12 w-12 text-purple-400 group-hover:drop-shadow-[0_0_15px_rgba(147,51,234,0.8)]" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-purple-300 text-center mb-4">Ambiance conviviale</h3>
              <p className="text-gray-400 text-center leading-relaxed">
                Partagez des moments chaleureux dans une atmosphère bienveillante où chacun trouve sa place et s'épanouit
              </p>
            </Card>

            {/* Feature 3 */}
            <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8 hover:border-purple-400/60 transition-all hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.4)] group">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-purple-500/20 rounded-full group-hover:bg-purple-500/30 transition-all">
                  <Sparkles className="h-12 w-12 text-purple-400 group-hover:drop-shadow-[0_0_15px_rgba(147,51,234,0.8)]" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-purple-300 text-center mb-4">Univers variés</h3>
              <p className="text-gray-400 text-center leading-relaxed">
                D&D, City of Mist, Monster of The Week, Shadowrun Anarchy, Cyberpunk Red, Call of Cthulhu... Explorez une multitude d'univers fantastiques
              </p>
            </Card>

            {/* Feature 4 */}
            <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8 hover:border-purple-400/60 transition-all hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.4)] group">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-purple-500/20 rounded-full group-hover:bg-purple-500/30 transition-all">
                  <Calendar className="h-12 w-12 text-purple-400 group-hover:drop-shadow-[0_0_15px_rgba(147,51,234,0.8)]" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-purple-300 text-center mb-4">Apprentissage ludique</h3>
              <p className="text-gray-400 text-center leading-relaxed">
                Développez votre créativité, votre imagination et vos compétences sociales dans un cadre ludique et stimulant
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section - BLACK BACKGROUND */}
      <section className="py-12 md:py-20 px-4 bg-black">
        <div className="container mx-auto max-w-3xl">
          <Card className="bg-gradient-to-br from-purple-900/60 via-purple-800/40 to-black border-2 border-purple-500/50 p-6 md:p-12 text-center space-y-4 md:space-y-6 hover:drop-shadow-[0_0_30px_rgba(147,51,234,0.5)] transition-all">
            <h2 className="text-2xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-purple-500 tracking-wide">
              L'Aventure ? C'est par ici !
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Prêt à lancer les dés ? Tu souhaites rejoindre l'association ? Alors clique sur le lien ci-dessous pour nous contacter.
            </p>
            <Link 
              to="/contact"
              className="inline-block"
              data-testid="start-adventure-contact-link"
            >
              <Button className="px-6 md:px-10 py-4 md:py-6 text-base md:text-lg font-bold bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white neon-button transform hover:scale-105 transition-all">
                <Mail className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                Commencer l'aventure
              </Button>
            </Link>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;